/**
 * Reprint stickers through RxPrint: regenerate an order's product stickers (ZPL) with the current
 * template and print them at the order's shipping location — at the user's shipping lane when
 * the order ships from the lane's location, else at the document's default printers. Server side:
 * libs/services/src/order/regenerate-stickers.ts.
 */
import { gql } from '@apollo/client';

export const REGENERATE_ORDER_STICKERS = gql`
  mutation WarehouseRegenerateOrderStickers($orderId: ID!, $companyId: ID!) {
    salesOrderRegenerateStickers(orderId: $orderId, companyId: $companyId) {
      orderId
      vendorSalesOrderNumber
      descriptor
      status
      willPrint
      message
    }
  }
`;

/** Shipping and every vendor status after it. Mirrors STICKER_REGENERATE_STATUSES. */
export const STICKER_REPRINT_STATUSES = ['shipping', 'shipped', 'customer_pickup', 'completed'] as const;

export const isStickerReprintStatus = (vendorStatus?: string | null): boolean =>
  (STICKER_REPRINT_STATUSES as readonly string[]).includes(String(vendorStatus ?? ''));

export type StickerReprintOrderResult = {
  orderId: string;
  vendorSalesOrderNumber?: string | null;
  descriptor?: string | null;
  status: string;
  willPrint: boolean;
  message?: string | null;
};

/** `alert(title, message, seconds, type)` arguments. */
export type StickerReprintNotice = {
  title: string;
  message: string;
  type: 'success' | 'warning' | 'error';
};

export function stickerReprintOrderNotice(
  result: StickerReprintOrderResult
): StickerReprintNotice {
  if (result.willPrint) {
    return {
      title: 'Stickers sent to RxPrint',
      message: 'They are being regenerated and will print shortly.',
      type: 'success'
    };
  }
  return {
    title: 'Stickers regenerated',
    message:
      result.message || 'They are being regenerated, but will not print through RxPrint.',
    type: 'warning'
  };
}
